import React, { useCallback, useEffect, useMemo, useState } from "react";
import { makeStyles } from "tss-react/mui";
import { Button, LinearProgress, Switch, TextField, Typography } from "@mui/material";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "../../services/toast";
import { i18n } from "../../translate/i18n";

const MAX_USAGE_LENGTH = 500;

const useStyles = makeStyles()((theme) => ({
  title: {
    fontSize: 14,
    fontWeight: 600,
  },
  help: {
    fontSize: 12.5,
    color: theme.palette.text.secondary,
    margin: theme.spacing(0.5, 0, 2),
    maxWidth: 640,
  },
  list: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.25),
  },
  card: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 10,
    padding: theme.spacing(1.25, 1.5),
    transition: "border-color .15s ease",
  },
  cardEnabled: {
    borderColor: theme.palette.primary.main,
  },
  cardHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.25),
  },
  fileIcon: {
    width: 34,
    height: 34,
    borderRadius: 8,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: theme.palette.action.hover,
    color: theme.palette.primary.main,
    flexShrink: 0,
  },
  name: {
    fontSize: 14,
    fontWeight: 600,
    lineHeight: 1.3,
  },
  meta: {
    fontSize: 12,
    color: theme.palette.text.secondary,
  },
  usageField: {
    marginTop: theme.spacing(1.5),
  },
  empty: {
    fontSize: 13,
    color: theme.palette.text.secondary,
    padding: theme.spacing(2, 0),
  },
  footer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(2),
  },
  footerNote: {
    fontSize: 12.5,
    color: theme.palette.text.secondary,
  },
  footerWarning: {
    fontSize: 12.5,
    color: theme.palette.warning.main,
  },
}));

const buildRows = (documents, selectedIds, usage) =>
  documents.map((document) => ({
    id: document.rootDocumentId,
    enabled: selectedIds.includes(document.rootDocumentId),
    usage: usage?.[String(document.rootDocumentId)] || "",
  }));

const PromptKnowledgeTab = ({ promptId, selectedIds, usage, onSaved }) => {
  const { classes } = useStyles();
  const [documents, setDocuments] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const selected = useMemo(() => (selectedIds || []).map(Number), [selectedIds]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/knowledge/published");
      setDocuments(data || []);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const original = useMemo(() => buildRows(documents, selected, usage), [documents, selected, usage]);

  useEffect(() => {
    setRows(original);
  }, [original]);

  const updateRow = (id, changes) => setRows((prev) => prev.map((row) => (row.id === id ? { ...row, ...changes } : row)));

  const changed = JSON.stringify(rows) !== JSON.stringify(original);
  const missingUsage = rows.some((row) => row.enabled && !row.usage.trim());

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/knowledge/prompts/${promptId}`, {
        documents: rows
          .filter((row) => row.enabled || row.usage.trim())
          .map((row) => ({ id: row.id, usage: row.usage, enabled: row.enabled })),
      });
      toast.success(i18n.t("knowledge.promptTab.saved"));
      await onSaved?.();
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const rowFor = (document) => rows.find((row) => row.id === document.rootDocumentId) || { enabled: false, usage: "" };

  return (
    <div>
      <Typography className={classes.title}>{i18n.t("knowledge.promptTab.title")}</Typography>
      <Typography className={classes.help}>{i18n.t("knowledge.promptTab.help")}</Typography>
      {loading ? (
        <LinearProgress />
      ) : documents.length === 0 ? (
        <Typography className={classes.empty}>{i18n.t("knowledge.promptTab.empty")}</Typography>
      ) : (
        <>
          <div className={classes.list}>
            {documents.map((document) => {
              const row = rowFor(document);
              return (
                <div key={document.rootDocumentId} className={`${classes.card} ${row.enabled ? classes.cardEnabled : ""}`}>
                  <div className={classes.cardHeader}>
                    <Switch
                      size="small"
                      checked={row.enabled}
                      onChange={(e) => updateRow(document.rootDocumentId, { enabled: e.target.checked })}
                    />
                    <div className={classes.fileIcon}>
                      <PictureAsPdfOutlinedIcon fontSize="small" />
                    </div>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <Typography className={classes.name} noWrap>
                        {document.name}
                      </Typography>
                      <Typography className={classes.meta} noWrap>
                        {[
                          `v${document.version}`,
                          i18n.t("knowledge.rulesCount", { count: document.publishedCount || 0 }),
                          document.originalName,
                        ].join(" · ")}
                      </Typography>
                    </div>
                  </div>
                  {row.enabled && (
                    <TextField
                      className={classes.usageField}
                      label={i18n.t("knowledge.promptTab.whenToUse")}
                      multiline
                      minRows={2}
                      maxRows={5}
                      fullWidth
                      size="small"
                      autoFocus={!row.usage}
                      error={!row.usage.trim()}
                      placeholder={i18n.t("knowledge.promptTab.whenToUsePlaceholder")}
                      value={row.usage}
                      onChange={(e) =>
                        updateRow(document.rootDocumentId, { usage: e.target.value.slice(0, MAX_USAGE_LENGTH) })
                      }
                      helperText={`${row.usage.length}/${MAX_USAGE_LENGTH}`}
                    />
                  )}
                </div>
              );
            })}
          </div>
          <div className={classes.footer}>
            {missingUsage ? (
              <span className={classes.footerWarning}>{i18n.t("knowledge.promptTab.missingUsage")}</span>
            ) : (
              changed && <span className={classes.footerNote}>{i18n.t("knowledge.promptTab.unsaved")}</span>
            )}
            <Button variant="contained" onClick={save} disabled={saving || !changed || missingUsage}>
              {i18n.t("knowledge.save")}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default PromptKnowledgeTab;
